/**
 * /app/{slug}/hr/offboarding/letters — Offboarding letter template library.
 *
 * Sister sub-route of `/hr/offboarding` (Cycles); they render the same
 * `<SubRouteTabs>` strip (SOP §1.4 — page-internal Sub-route Tabs
 * pattern). The Letters tab is the tenant-scoped template library for
 * warning, probation-confirmation, relieving, experience, and F&F
 * letters — every tenant has its own copies; deletes are blocked once
 * a template is sent on any cycle.
 *
 * History:
 *  - Phase 3f (2026-06-01) — this file was a `throw redirect()` to the
 *    Offboarding hub with `?tab=letters` (Cycles + Letters lived in
 *    one route, internal Tabs).
 *  - 2026-06-04 — promoted to a real route under SOP §1.4. The redirect
 *    is gone; `/hr/offboarding/letters` IS the Letters page now.
 */

import { createFileRoute, useParams } from "@tanstack/react-router";
import { z } from "zod";
import { HrModuleShell } from "@/components/shared/hr-module-shell";
import { OffboardingRoleGate } from "@/components/offboarding/offboarding-role-gate";
import { LettersTab } from "@/components/offboarding/letters-tab";
import { OffboardingSubRouteTabs } from "@/components/offboarding/sub-route-tabs";

// ?employee_id=&letter_type= pre-select the library (the employee page's
// "View letters" links here filtered to that person — people QA #10).
const lettersSearchSchema = z.object({
  employee_id: z.coerce.number().int().positive().optional().catch(undefined),
  letter_type: z.string().optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hr/offboarding/letters/",
)({
  validateSearch: lettersSearchSchema,
  component: OffboardingLettersRoute,
});

function OffboardingLettersRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const search = Route.useSearch();

  return (
    <HrModuleShell
      title="Letters"
      description="Track every employee exit: cycles, F&F, and letters."
    >
      <OffboardingRoleGate>
        <OffboardingSubRouteTabs tenantSlug={tenantSlug} className="mb-4" />
        <LettersTab
          initialEmployeeId={search.employee_id}
          initialLetterType={search.letter_type}
        />
      </OffboardingRoleGate>
    </HrModuleShell>
  );
}
